'use client'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import FlightLandIcon from '@mui/icons-material/FlightLand'
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import SearchIcon from '@mui/icons-material/Search'
import StarIcon from '@mui/icons-material/Star'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import { Box, Button, IconButton, Paper, Stack, SwipeableDrawer, Tab, Tabs, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React from 'react'

interface SearchHeaderProps {
    open: boolean
    onOpen: () => void
    onClose: () => void
    tripType: 'oneway' | 'roundtrip'
    onTripTypeChange: (type: 'oneway' | 'roundtrip') => void
    fromLocation: string
    toLocation: string
    departDate: string
    returnDate?: string
    travelers: string
    flightClass: string
    onFromClick: () => void
    onToClick: () => void
    onDateClick: () => void
    onTravelersClick: () => void
    onSearch: () => void
    onSwap: () => void
    hasError?: boolean
    srpResultsLoading?: boolean
}

const SearchHeader: React.FC<SearchHeaderProps> = ({
    open,
    onOpen,
    onClose,
    tripType,
    onTripTypeChange,
    fromLocation,
    toLocation,
    departDate,
    returnDate,
    travelers,
    flightClass,
    onFromClick,
    onToClick,
    onDateClick,
    onTravelersClick,
    onSearch,
    onSwap,
    hasError,
    srpResultsLoading = false
}) => {
    const t = useTranslations('common')

    const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
        onTripTypeChange(newValue as 'oneway' | 'roundtrip')
    }

    return (
        <SwipeableDrawer
            anchor="top"
            open={open}
            onClose={onClose}
            onOpen={onOpen}
            disableSwipeToOpen={false}
            PaperProps={{
                sx: {
                    borderBottomLeftRadius: 16,
                    borderBottomRightRadius: 16,
                    height: 'auto',
                    minHeight: '470px'
                }
            }}
        >
            <Box sx={{ px: 2 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.5,
                        height: '56px'
                    }}
                >
                    <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>{t('updateSearch')}</Typography>
                </Box>

                {/* Trip Type Tabs */}
                <Box sx={{ mb: 2 }}>
                    <Tabs
                        value={tripType}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            minHeight: 44,
                            height: 44,
                            '& .MuiTabs-indicator': {
                                height: 3,
                                backgroundColor: 'primary.main'
                            },
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                minHeight: 44,
                                height: 44,
                                color: 'text.secondary',
                                transition: 'all 0.2s',
                                '&.Mui-selected': {
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    bgcolor: '#f0f4ff'
                                }
                            }
                        }}
                    >
                        <Tab label={t('oneWay')} value="oneway" />
                        <Tab label={t('roundTrip')} value="roundtrip" />
                    </Tabs>
                </Box>

                {/* From and To with Swap Button */}
                <Box sx={{ position: 'relative' }}>
                    {/* From Location */}
                    <Paper
                        elevation={0}
                        onClick={onFromClick}
                        sx={{
                            py: 0.5,
                            px: 1,
                            mb: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            height: '55px',
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                            <FlightTakeoffIcon color="action" sx={{ fontSize: '20px' }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                    {t('fromAirportPlaceholder')}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: hasError ? 'error.main' : 'inherit'
                                    }}
                                >
                                    {fromLocation}
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>

                    {/* Swap Button */}
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                        }}
                    >
                        <IconButton
                            onClick={onSwap}
                            sx={{
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                            }}
                            size="small"
                        >
                            <SwapVertIcon sx={{ color: 'text.secondary' }} />
                        </IconButton>
                    </Box>

                    {/* To Location */}
                    <Paper
                        elevation={0}
                        onClick={onToClick}
                        sx={{
                            py: 0.5,
                            px: 1,
                            mb: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            height: '55px',
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                            <FlightLandIcon color="action" sx={{ fontSize: '20px' }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                    {t('toAirportPlaceholder')}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: hasError ? 'error.main' : 'inherit'
                                    }}
                                >
                                    {toLocation}
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>

                {/* Date Selection */}
                <Paper
                    elevation={0}
                    onClick={onDateClick}
                    sx={{
                        py: 0.5,
                        px: 1,
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        height: '55px',
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                            bgcolor: 'action.hover'
                        }
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <CalendarMonthIcon color="action" sx={{ fontSize: '20px' }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                {tripType === 'roundtrip' ? `${t('departure')} - ${t('return')}` : t('departure')}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                {tripType === 'roundtrip' && returnDate ? `${departDate} - ${returnDate}` : departDate}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {/* Travelers and Class */}
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <Paper
                        elevation={0}
                        onClick={onTravelersClick}
                        sx={{
                            p: 2,
                            flex: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <PersonOutlineIcon color="action" sx={{ fontSize: '20px' }} />
                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{travelers}</Typography>
                        </Stack>
                    </Paper>

                    <Paper
                        elevation={0}
                        onClick={onTravelersClick}
                        sx={{
                            p: 2,
                            flex: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <StarIcon sx={{ color: 'action.active', fontSize: '20px' }} />
                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{t(flightClass)}</Typography>
                        </Stack>
                    </Paper>
                </Stack>

                {/* Search Button */}
                <Button
                    disabled={srpResultsLoading}
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<SearchIcon />}
                    onClick={onSearch}
                    sx={{
                        height: '40px',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '16px'
                    }}
                >
                    {t('modifySearch')}
                </Button>

                {/* Handle */}
                <Box
                    sx={{
                        width: '40px',
                        height: '4px',
                        bgcolor: 'grey.500',
                        borderRadius: '2px',
                        mx: 'auto',
                        mt: 1.5
                    }}
                />
            </Box>
        </SwipeableDrawer>
    )
}

export { SearchHeader }
export type { SearchHeaderProps }
